/**
 * Registration Context
 *
 * Drives the agent registration state machine.
 *
 * The invitation token from the emailed link is the only thing that proves the
 * person on this screen is the invitee. It is passed to the backend on every
 * call and never persisted anywhere - not in Firestore, not in sessionStorage.
 * The server sets the password and hands back a custom sign-in token, so the
 * browser never handles a credential it did not choose itself.
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import { AgentService } from '../services/agentService';
import {
	RegistrationState,
	registrationReducer,
	AgentData
} from '../types/registration';

/** Kept in step with MIN_PASSWORD_LENGTH in the backend handler. */
const MIN_PASSWORD_LENGTH = 10;

interface RegistrationContextType {
	state: RegistrationState;
	isLoading: boolean;
	error: string | null;

	// Actions
	validateInvitation: (email: string, token: string) => Promise<void>;
	submitPassword: (
		email: string,
		token: string,
		password: string,
		confirmPassword: string,
		phoneNumber: string
	) => Promise<void>;
	clearError: () => void;
	reset: () => void;
}

const RegistrationContext = createContext<RegistrationContextType | null>(null);

export const useRegistration = () => {
	const context = useContext(RegistrationContext);
	if (!context) {
		throw new Error('useRegistration must be used within a RegistrationProvider');
	}
	return context;
};

interface RegistrationProviderProps {
	children: React.ReactNode;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({ children }) => {
	const [state, dispatch] = useReducer(registrationReducer, { type: 'IDLE' });
	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setLocalError] = React.useState<string | null>(null);

	/**
	 * Confirm the link is real before asking for a password.
	 */
	const validateInvitation = useCallback(async (email: string, token: string) => {
		try {
			setIsLoading(true);
			setLocalError(null);
			dispatch({ type: 'START_VALIDATION' });

			const invitation = await AgentService.validateInvitation(email, token);

			dispatch({
				type: 'INVITATION_VALIDATED',
				agent: {
					email: invitation.email,
					firstName: invitation.firstName,
					role: invitation.role as AgentData['role']
				}
			});
		} catch (err: any) {
			const errorMessage = err?.message || 'This invitation link is not valid.';
			setLocalError(errorMessage);
			dispatch({ type: 'INVITATION_FAILED', error: errorMessage });
		} finally {
			setIsLoading(false);
		}
	}, []);

	/**
	 * Redeem the invitation and sign in.
	 */
	const submitPassword = useCallback(async (
		email: string,
		token: string,
		password: string,
		confirmPassword: string,
		phoneNumber: string
	) => {
		if (state.type !== 'PASSWORD_SETUP') {
			return;
		}

		if (!password || password.length < MIN_PASSWORD_LENGTH) {
			setLocalError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
			return;
		}

		if (password !== confirmPassword) {
			setLocalError('Passwords do not match');
			return;
		}

		try {
			setIsLoading(true);
			setLocalError(null);

			// The server verifies the token, sets the password, activates the
			// agent, and returns a one-shot token to sign in with.
			const result = await AgentService.completeAgentRegistration(email, token, password);
			await signInWithCustomToken(auth, result.customToken);

			if (phoneNumber) {
				await AgentService.updateAgent(result.agentId, { phone: phoneNumber });
			}

			dispatch({ type: 'REGISTRATION_COMPLETE' });
		} catch (err: any) {
			let errorMessage = 'Registration failed. Please try again.';

			if (err?.code === 'auth/weak-password') {
				errorMessage = 'Choose a stronger password.';
			} else if (err?.code === 'auth/too-many-requests') {
				errorMessage = 'Too many attempts. Wait a few minutes and try again.';
			} else if (err?.message) {
				errorMessage = err.message;
			}

			setLocalError(errorMessage);
			dispatch({ type: 'ERROR_OCCURRED', error: errorMessage });
		} finally {
			setIsLoading(false);
		}
	}, [state]);

	const clearError = useCallback(() => {
		setLocalError(null);
	}, []);

	const reset = useCallback(() => {
		dispatch({ type: 'START_VALIDATION' });
		setLocalError(null);
	}, []);

	const value: RegistrationContextType = React.useMemo(() => ({
		state,
		isLoading,
		error,
		validateInvitation,
		submitPassword,
		clearError,
		reset
	}), [state, isLoading, error, validateInvitation, submitPassword, clearError, reset]);

	return (
		<RegistrationContext.Provider value={value}>
			{children}
		</RegistrationContext.Provider>
	);
};
